import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Button, SvgIcon } from '@material-ui/core';
import { MonthPicker, LineSelector, FilterMenu, AttributesSelector } from '..';
import {
  SVG_ICON_PATHS,
  DISPLAY_MESSAGES,
  USER_OPERATIONS,
  APPLICATION_VARIANTS,
} from '../../constants';
import defaultStyles from './ToolBar.module.css';
import jlrStyles from './ToolBarJlr.module.css';
import { FilterIcon } from '../../icons';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { AiFillFile } from 'react-icons/ai';
import { IoMdCloudDownload } from 'react-icons/io';
import { IoMdCloudUpload } from 'react-icons/io';
import { MdClose } from 'react-icons/md';
import { Prompt } from 'react-router-dom';
import {
  callFunctionConditionallyWithWarningCurry,
  getFilterOptionsByBU,
  setStylesAccordingToPlant,
  exportToExcel,
} from '../../utils/utils';
import AuthEnabledButton from '../AuthEnabledButton/AuthEnabledButton';
import config from '../../config/config';
import { usePlantAndLinesStore } from '../../providers/PlantAndLinesStore/PlantAndLinesStore';
import { downloadCsv } from '../../utils/utils';
import { parseAsync } from 'json2csv';
import { buildErrorMessage } from '../../apis/calls';

const ToolBar = ({
  applyColumnsSelection,
  applyFilters,
  disableFilter,
  disableMonthPicker,
  disableUpload,
  handleFileUpload,
  handleMonthSelect,
  hasActiveEdits,
  lastUpdatedDate,
  optionalColumns,
  planData,
  restrictNavigationDuringEditing,
  selectedOptionalCols,
  callingAverage,
  line,
}) => {
  const timeString = moment(lastUpdatedDate).format('hh:mma');
  const dateString = moment(lastUpdatedDate).format('DD MMMM YYYY');

  const [styles, setStyles] = useState(defaultStyles);
  const [plant] = usePlantAndLinesStore().plantState;

  const filterRef = useRef(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [
    isDownloadOrUploadDialogOpen,
    setIsDownloadOrUploadDialogOpen,
  ] = useState(false);

  const handleFilterOpen = () => setIsFilterOpen(true);
  const handleFilterClose = () => setIsFilterOpen(false);
  const handleFilter = filters => {
    applyFilters(filters);
    handleFilterClose();
  };
  const showDownloadAndUploadDialog = () => {
    setIsDownloadOrUploadDialogOpen(true);
  };
  const handleDonloadOrUploadDialogueClose = () => {
    setIsDownloadOrUploadDialogOpen(false);
  };

  const handleDownloadTemplateButtonClick = () => {
    const link = document.createElement('a');
    link.setAttribute('download', `Day-wise-upload-plan-template.xlsx`);
    exportToExcel(config.APPLICATION_VARIANT).then(dataUri => {
      link.href = dataUri;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setIsDownloadOrUploadDialogOpen(false);
    });
  };
  const handleDayWisePlanDownload = async (planData, selectedOptionalCols) => {
    if (!planData || planData.data.length === 0) return;
    planData.data = planData.data.map(row => ({
      ...row,
      ...(row.attributes || {})
    }));

    const optionalAttributeColumns = [
      { title: "Family", field: "family" },
      { title: "Order Frequency", field: "orderFrequency" },
      { title: "Power Steering", field: "powerSteering" },
      { title: "Build Card Number", field: "buildCardNumber" },
      { title: "Market", field: "market" },
      { title: "Category", field: "category" },
      { title: "Country", field: "country" },
      { title: "Defence", field: "defence" },
      { title: "FBV", field: "fbv" },
      { title: "FBV Vendor Code", field: "fbvVendorCode" },
      { title: "FBV Vendor Name", field: "fbvVendorName" },
      { title: "Colour", field: "colour" },
      { title: "Evr Number", field: "evrNumber" }
    ];

    const chassisPlanHeaders = [
      { label: 'Vc', value: ({ vc }) => vc || '' },
      { label: 'Description', value: ({ description }) => description || '' },
      { label: 'Target', value: ({ total }) => total || '' },
      { label: 'Balance', value: ({ balance }) => balance || '' }
    ];

    chassisPlanHeaders.push(
      ...optionalAttributeColumns
        .filter(attr => selectedOptionalCols.includes(attr.title))
        .map(attr => ({
          label: attr.title,
          value: row => row[attr.field] ?? ''
        }))
    );
    try {
      const opts = { fields:chassisPlanHeaders };
      const csv = await parseAsync(planData.data, opts);
      const fileName = `ChassisPlan_${plant.label}_${line.label}.csv`;
      downloadCsv(csv, fileName);
    } catch (error) {
      console.error(
        'Failed to generate or download the Chassis Planning Line Out CSV report, error:' +
        buildErrorMessage(error),
      )
    }
  };
  const renderUploadButton = () => {
    // TODO: fix styling to not need div
    if (disableUpload) return <div className={styles.uploadButton} />;
    return (
      <div>
        <AuthEnabledButton
          operation={USER_OPERATIONS.CREATE_CHASSIS_PLAN_QUANTITY}
          isIconButton
          className={styles.uploadButton}
          onClick={showDownloadAndUploadDialog}
          data-testid="upload-btn"
        >
          <SvgIcon className={styles.uploadIcon}>
            <path d={SVG_ICON_PATHS.uploadIcon} />
          </SvgIcon>
          <span className={styles.label}>Upload</span>
        </AuthEnabledButton>
        <AuthEnabledButton
          operation={USER_OPERATIONS.CREATE_CHASSIS_PLAN_QUANTITY}
          isIconButton
          className={styles.downloadButton}
          onClick={() => handleDayWisePlanDownload(planData, selectedOptionalCols)}
          data-testid="download-btn"
        >
          <SvgIcon className={styles.downloadBtnIcon}>
            <path d={SVG_ICON_PATHS.downloadIcon} />
          </SvgIcon>
          <span className={styles.label}>Download</span>
        </AuthEnabledButton>

        <Dialog
          className={styles.uploadPopip}
          open={isDownloadOrUploadDialogOpen}
          onClose={handleDonloadOrUploadDialogueClose}
          aria-labelledby="draggable-dialog-title"
        >
          <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
            <span>
              <AiFillFile className={styles.fileIcon} />
              <span className={styles.title}>Plan </span>
              <MdClose
                className={styles.cancelIcon}
                onClick={handleDonloadOrUploadDialogueClose}
              />
            </span>
          </DialogTitle>
          <DialogContent>
            <div>
              <Button
                className={styles.downloadTemplateButton}
                onClick={handleDownloadTemplateButtonClick}
              >
                <IoMdCloudDownload className={styles.downloadIcon} />

                <span className={styles.label}>
                  <span className={styles.mainText}>
                    Template <sup>**</sup>
                  </span>
                  <span className={styles.subText}>(Excel file)</span>
                </span>
              </Button>

              <Button className={styles.uploadCsvButton}>
                <IoMdCloudUpload className={styles.uploadCsvIcon} />
                <span className={styles.label}>
                  <label className={styles.upTxt} htmlFor="upload-csv">
                    Upload plan
                  </label>
                  <label className={styles.subText}>(CSV file)</label>
                </span>
                <input
                  className={styles.input}
                  type="file"
                  id="upload-csv"
                  accept=".csv"
                  onClick={e => e.stopPropagation()}
                  onChange={e => {
                    const elem = e.target;
                    handleFileUpload(elem.files[0]);
                    elem.value = ''; // reset input element
                    setIsDownloadOrUploadDialogOpen(false);
                  }}
                />
              </Button>
            </div>
            <p className={styles.message}>
              **Please use the Template for uploading new/revised Plans in the
              system.
            </p>
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  const CallingAverageDisplay = ({ average }) => {
    return (
      <div className={styles.callingCount}>
        <span className={styles.calling}> Calling average</span>
        <span className={styles.avgCount}>
          {average === null ? '- -' : average}
        </span>
      </div>
    );
  };

  useEffect(() => {
    setStylesAccordingToPlant(plant, setStyles, defaultStyles, jlrStyles);
  }, [plant]);

  return (
    <>
      <div className={styles.toolBar}>
        {renderUploadButton()}
        {callingAverage !== undefined && (
          <CallingAverageDisplay average={callingAverage} />
        )}
        {!disableMonthPicker && (
          <MonthPicker
            handleSelect={callFunctionConditionallyWithWarningCurry(
              handleMonthSelect,
              hasActiveEdits,
            )}
          />
        )}

        <AttributesSelector
          columns={optionalColumns}
          selectedOptionalCols={selectedOptionalCols}
          applyColumnsSelection={applyColumnsSelection}
          isShiftPresent={false}
        />
        {!disableFilter && (
          <Button
            className={styles.toolButton}
            ref={filterRef}
            onClick={handleFilterOpen}
          >
            <FilterIcon
              className={styles.toolIcon}
              style={{ fontSize: '19px', marginTop: '2px' }}
              fontSize="small"
            />
            Filter
          </Button>
        )}
        <FilterMenu
          open={isFilterOpen}
          handleClose={handleFilterClose}
          anchorEl={filterRef.current}
          handleFilter={handleFilter}
          filterOptions={getFilterOptionsByBU()}
          rows={planData.data}
        />
        <LineSelector hasActiveEdits={hasActiveEdits} />
      </div>
      <span className={styles.captionSmall}>
        {`Last Refreshed: ${timeString} (IST) ${dateString}`}
      </span>
      {restrictNavigationDuringEditing && (
        <Prompt
          when={hasActiveEdits}
          message={() => DISPLAY_MESSAGES.NAVIGATION_WARNING}
        />
      )}
    </>
  );
};

ToolBar.propTypes = {
  optionalColumns: PropTypes.array,
  applyColumnsSelection: PropTypes.func,
  lastUpdatedDate: PropTypes.instanceOf(moment),
  handleFileUpload: PropTypes.func,
  disableUpload: PropTypes.bool,
  selectedOptionalCols: PropTypes.array.isRequired,
  planData: PropTypes.object.isRequired,
  applyFilters: PropTypes.func.isRequired,
  handleMonthSelect: PropTypes.func,
  disableMonthPicker: PropTypes.bool,
  disableFilter: PropTypes.bool,
  restrictNavigationDuringEditing: PropTypes.bool.isRequired,
  hasActiveEdits: PropTypes.bool.isRequired,
  callingAverage: PropTypes.number,
  line: PropTypes.object,
};

export default ToolBar;
